import { Menu, MenuItem } from "@blueprintjs/core";
import React, { MutableRefObject } from "react";
import { useNavigate } from "react-router-dom";

import {
  Series,
  Team,
  useBulkChangeSeriesMutation,
  useBulkChangeTeamsMutation,
  useDuplicatePlayMutation,
  useEditPlayMutation,
  useGetSinglePlay,
} from "../../../redux/slices";
import { SportPlay } from "../../../types";
import { PlaysWithSelection } from "../hooks";
import styles from "./table-context-menu.module.scss";

export interface TableContextMenuProps<TPlay extends SportPlay> extends Pick<PlaysWithSelection, "selectedIds"> {
  playIdRef: MutableRefObject<string | null>;
  plays: TPlay[];
  teams: Team[] | undefined;
  series: Series[] | undefined;
  setActivePlayId: (id: string) => void;
  openEditPlayDetails: (play: SportPlay) => void;
  setPlaysToDelete: (plays: TPlay[]) => void;
  setPlaybookPlays: (plays: string[]) => void;
  setMobileAccessIds: (ids: string[]) => void;
}

interface UpdatePlayProps<TPlay extends SportPlay> {
  playId: string | null;
  newPlayProps: Partial<TPlay>;
}

export const TableContextMenu = <TPlay extends SportPlay>({
  playIdRef,
  plays,
  teams,
  series,
  setActivePlayId,
  openEditPlayDetails,
  selectedIds,
  setPlaysToDelete,
  setPlaybookPlays,
  setMobileAccessIds,
}: TableContextMenuProps<TPlay>) => {
  const navigate = useNavigate();

  const { editPlay } = useEditPlayMutation();
  const [bulkChangeTeams] = useBulkChangeTeamsMutation();
  const [bulkChangeSeries] = useBulkChangeSeriesMutation();
  const [duplicatePlay] = useDuplicatePlayMutation();
  const { data: playToEdit } = useGetSinglePlay(playIdRef.current);

  const updatePlay = <TUpdatePlay extends SportPlay>({ playId, newPlayProps }: UpdatePlayProps<TUpdatePlay>) => {
    if (playId) {
      if (playToEdit) editPlay({ play: { ...playToEdit, ...newPlayProps } });
    }
  };

  const numOfSelectedPlays = selectedIds.length;
  const multipleSelected = numOfSelectedPlays > 1;
  const isCurrentPlaySelected = (currentPlayId: string) => {
    return selectedIds.includes(currentPlayId);
  };

  return (
    <Menu className={styles.contextMenu}>
      {(!multipleSelected || (multipleSelected && !isCurrentPlaySelected(playIdRef.current as string))) && (
        <MenuItem
          text="Open Play"
          onClick={() => {
            if (playIdRef.current) {
              setActivePlayId(playIdRef.current);
              navigate(`/plays/${playIdRef.current}`);
            }
          }}
        />
      )}
      {(!multipleSelected || (multipleSelected && !isCurrentPlaySelected(playIdRef.current as string))) && (
        <MenuItem
          text="Duplicate Play"
          onClick={async () => {
            if (playIdRef.current) {
              const newPlay = await duplicatePlay({ playId: playIdRef.current }).unwrap();
              setActivePlayId(newPlay.id);
              navigate(`/plays/${newPlay.id}`);
            }
          }}
        />
      )}
      {(!multipleSelected || (multipleSelected && !isCurrentPlaySelected(playIdRef.current as string))) && (
        <MenuItem
          text="Edit Play Details"
          onClick={() => {
            if (playIdRef.current) {
              setActivePlayId(playIdRef.current);
              if (playToEdit) openEditPlayDetails(playToEdit);
            }
          }}
        />
      )}
      <MenuItem
        text={
          multipleSelected
            ? `Change (${isCurrentPlaySelected(playIdRef.current as string) ? numOfSelectedPlays : 1}) Teams To`
            : "Change Team To"
        }
      >
        {teams?.map((team) => (
          <MenuItem
            key={team.id}
            text={team.name}
            onClick={() => {
              if (multipleSelected && isCurrentPlaySelected(playIdRef.current as string))
                bulkChangeTeams({ teamId: team.team_id, playIds: selectedIds });
              else {
                updatePlay({ playId: playIdRef.current, newPlayProps: { team } });
              }
            }}
          />
        ))}
      </MenuItem>
      <MenuItem
        text={
          multipleSelected
            ? `Change (${isCurrentPlaySelected(playIdRef.current as string) ? numOfSelectedPlays : 1}) Series To`
            : "Change Series To"
        }
      >
        {series?.map((series) => (
          <MenuItem
            key={series.id}
            text={series.name}
            onClick={() => {
              if (multipleSelected && isCurrentPlaySelected(playIdRef.current as string))
                bulkChangeSeries({ seriesId: series.series_id, playIds: selectedIds });
              else updatePlay({ playId: playIdRef.current, newPlayProps: { series } });
            }}
          />
        ))}
      </MenuItem>
      <MenuItem
        text={`Create Playbook (${isCurrentPlaySelected(playIdRef.current as string) ? numOfSelectedPlays : 1})`}
        onClick={() => {
          if (multipleSelected && isCurrentPlaySelected(playIdRef.current as string)) {
            setPlaybookPlays(selectedIds);
          } else {
            setPlaybookPlays(playIdRef.current ? [playIdRef.current] : []);
          }
        }}
      />
      {multipleSelected && (
        <MenuItem
          text={`Edit Mobile Access (${isCurrentPlaySelected(playIdRef.current as string) ? numOfSelectedPlays : 1})`}
          onClick={() => setMobileAccessIds(selectedIds)}
        />
      )}
      <MenuItem
        text={
          multipleSelected
            ? `Delete (${isCurrentPlaySelected(playIdRef.current as string) ? numOfSelectedPlays : 1}) Plays`
            : "Delete Play"
        }
        onClick={() => {
          if (multipleSelected && isCurrentPlaySelected(playIdRef.current as string)) {
            const playsToDelete = plays.filter((play) => selectedIds.includes(play.id || ""));
            setPlaysToDelete(playsToDelete);
          } else {
            const playsToDelete = plays.filter((play) => play.id === playIdRef.current);
            setPlaysToDelete(playsToDelete);
          }
        }}
      />
    </Menu>
  );
};
